// Service Worker registration for DivePeaks PWA

class ServiceWorkerManager {
  private registration: ServiceWorkerRegistration | null = null;

  async register(): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) {
      return null;
    }

    try {
      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });


      // Handle updates
      this.registration.addEventListener('updatefound', () => {
        const newWorker = this.registration?.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New version available
              this.notifyUpdate();
            }
          });
        }
      });

      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        this.handleServiceWorkerMessage(event);
      });

      return this.registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }

  async unregister(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        return await registration.unregister();
      }
      return false;
    } catch (error) {
      console.error('Service Worker unregistration failed:', error);
      return false;
    }
  }

  async update(): Promise<void> {
    if (this.registration) {
      await this.registration.update();
    }
  }

  private notifyUpdate(): void {
    // Dispatch custom event for app to handle
    window.dispatchEvent(new CustomEvent('sw-update-available'));
  }

  private handleServiceWorkerMessage(event: MessageEvent): void {
    const { type, payload } = event.data || {};

    switch (type) {
      case 'CACHE_UPDATED':
        window.dispatchEvent(new CustomEvent('sw-cache-updated', { detail: payload }));
        break;
      case 'CACHE_INVALIDATED':
        window.dispatchEvent(new CustomEvent('sw-cache-invalidated', { detail: payload }));
        break;
      case 'FORCE_REFRESH_COMPLETE':
        window.dispatchEvent(new CustomEvent('sw-force-refresh-complete', { detail: payload }));
        break;
      case 'OFFLINE_READY':
        window.dispatchEvent(new CustomEvent('sw-offline-ready'));
        break;
      case 'CLIMB_DELETED':
        // A climb was deleted via a push notification action button.
        window.dispatchEvent(new CustomEvent('sw-climb-deleted', { detail: payload }));
        break;
      case 'QUEUE_PROCESSED':
        window.dispatchEvent(new CustomEvent('sw-queue-processed', { detail: payload }));
        break;
      default:
        break;
    }
  }

  // Send message to service worker
  sendMessage(type: string, payload?: unknown): void {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type, payload });
    }
  }

  // Skip waiting for new service worker
  skipWaiting(): void {
    this.sendMessage('SKIP_WAITING');
  }

  // Force refresh a specific API URL cache entry
  forceRefresh(url: string): void {
    this.sendMessage('FORCE_REFRESH', { url });
  }

  // Force refresh all cached data for a category ('gyms', 'climbs', 'stats')
  forceRefreshCategory(category: string): void {
    this.sendMessage('FORCE_REFRESH', { category });
  }

  // Clear the entire API cache
  clearApiCache(): void {
    this.sendMessage('CLEAR_API_CACHE');
  }

  // Clear all caches (static + API)
  async clearCache(): Promise<boolean> {
    return new Promise((resolve) => {
      const channel = new MessageChannel();
      channel.port1.onmessage = (event) => {
        resolve(event.data.success);
      };

      this.sendMessage('CLEAR_CACHE');
    });
  }
}

// Export singleton instance
export const serviceWorkerManager = new ServiceWorkerManager();

// Auto-register on load (only in production)
if (import.meta.env.PROD) {
  window.addEventListener('load', () => {
    serviceWorkerManager.register();
  });
}
